import { Modal } from "@circle-react-uikit/ModalV2";
import { Tab } from "@circle-react-uikit/TabV2";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { EditSpaceGroupForm } from "@circle-react/components/SpaceGroups/EditFormV2";
import { DeleteSpaceGroupModal } from "@circle-react/components/SpaceGroups/Header/DeleteSpaceGroupModal";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { Badge } from "@/react/components/SettingsApp/CompassCompat/Badge";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";

import { useEditSpaceGroupModal } from "./hooks/useEditSpaceGroupModal";
import { SpaceGroupMembers } from "./SpaceGroupMembers";
import {
  EDIT_SPACE_GROUP_FORM_ID,
  type EditSpaceGroupModalProps,
} from "./types";

const availableTabs: ["Details", "Members"] = ["Details", "Members"];

export const EditSpaceGroupModalUIKitComponent = ({
  spaceGroup: _spaceGroup,
  tab = availableTabs[0],
}: EditSpaceGroupModalProps) => {
  const [currentTab, setCurrentTab] = useState(tab);
  const modal = useModal();
  const isSmScreen = useSmScreenMediaQuery();
  const {
    activeSpaceGroupMembersCount,
    isEditing,
    isLoadingSpaceGroupResource,
    onSubmit,
    shouldShowConfirmDeleteModal,
    spaceGroup,
    toggleShouldShowConfirmDeleteModal,
  } = useEditSpaceGroupModal({
    spaceGroup: _spaceGroup,
    onClose: () => {
      void modal.hide();
    },
  });

  const isDetails = currentTab === "Details";
  const isMembers = currentTab === "Members";

  return (
    <>
      <Modal
        isOpen={modal.visible}
        onClose={modal.hide}
        contentPortalId="edit-space-group-content-portal"
        title={t("space_groups.form.edit.title")}
      >
        <Modal.Overlay />
        <Modal.Content size="xl">
          <Modal.Header>
            <Modal.Title size="md">
              {t("space_groups.form.edit.title")}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton onClick={modal.hide} />
            </Modal.HeaderActions>
          </Modal.Header>
          <Modal.Body
            id="scrollable-content"
            className={classNames({
              "px-0 py-0": isMembers,
            })}
          >
            <Tab.Group
              selectedIndex={availableTabs.indexOf(currentTab)}
              onChange={tabIndex => setCurrentTab(availableTabs[tabIndex])}
            >
              <Tab.List>
                <Tab>{t("space_groups.details")}</Tab>
                <Tab>
                  <div className="flex items-center gap-2">
                    {t("space_groups.members")}{" "}
                    {activeSpaceGroupMembersCount != null && (
                      <Badge
                        square
                        label={String(activeSpaceGroupMembersCount)}
                        variant="secondary"
                        inverted
                      />
                    )}
                  </div>
                </Tab>
              </Tab.List>
              <Tab.Panels className="mt-4">
                <Tab.Panel>
                  <EditSpaceGroupForm
                    formId={EDIT_SPACE_GROUP_FORM_ID}
                    onSubmit={onSubmit}
                    defaultValues={spaceGroup}
                    loading={isLoadingSpaceGroupResource}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <SpaceGroupMembers spaceGroup={spaceGroup} />
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </Modal.Body>
          {isDetails && (
            <Modal.Footer className="flex w-full justify-between gap-4 md:gap-0">
              <Button
                type="button"
                variant="danger-outline"
                className="hidden md:inline-block"
                onClick={toggleShouldShowConfirmDeleteModal}
              >
                {t("space_groups.delete_space_group")}
              </Button>
              <Button
                type="button"
                full
                variant="secondary"
                className="inline-block text-center md:hidden"
                onClick={() => {
                  void modal.hide();
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                full={isSmScreen}
                type="submit"
                variant="circle"
                form={EDIT_SPACE_GROUP_FORM_ID}
                disabled={isEditing}
              >
                {isEditing
                  ? t("space_groups.form.edit.submitting")
                  : t("save_changes")}
              </Button>
            </Modal.Footer>
          )}
        </Modal.Content>
      </Modal>
      <DeleteSpaceGroupModal
        spaceGroupId={spaceGroup.id}
        show={shouldShowConfirmDeleteModal}
        onClose={toggleShouldShowConfirmDeleteModal}
        onSuccess={modal.hide}
      />
    </>
  );
};
